import { Button, Stack, TextField, Typography } from '@mui/material';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import React, { useState, useEffect, useRef } from 'react';
import { InboxChat } from 'types/user';
import dayjs from 'dayjs';
import { useDimension } from 'hooks/useDimension';

// Helper function to render text with URLs as anchor tags
export function renderWithLinks(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline', color: 'blue' }}>
          View
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
interface ChatBoxProps {
  notes?: InboxChat[];
  onSendMessage: (message: string, idFile: number) => void;
  handleDeleteMessage: (chatId: number) => void;
  hasMoreMessage: boolean;
  fetchMoreChatDetail: () => void;
  isMobile?: boolean;
  isMoreChat?: boolean;
}

export const ChatBox: React.FC<ChatBoxProps> = ({
  notes,
  onSendMessage,
  handleDeleteMessage,
  hasMoreMessage,
  isMoreChat,
  isMobile,
  fetchMoreChatDetail
}) => {
  const { height } = useDimension();
  const [input, setInput] = useState('');
  // Add state
  const [replyingNote, setReplyingNote] = useState<InboxChat | null>(null);
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});
  const [showToggleMap, setShowToggleMap] = useState<Record<number, boolean>>({});
  const refsMap = useRef<Record<number, HTMLElement | null>>({});
  const chatBottomRef = useRef<HTMLDivElement | null>(null);

  // Handler for reply button
  const handleReplyMessage = (note: InboxChat) => {
    setReplyingNote(note);
    setTimeout(() => {
      chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  const handleCancelReply = () => setReplyingNote(null);
  useEffect(() => {
    setShowToggleMap((prev) => {
      const newMap: Record<number, boolean> = { ...prev };
      notes?.forEach((note) => {
        const el = refsMap.current[note.id!];
        if (el) {
          const isOverflow = el.scrollHeight > el.clientHeight;
          if (!newMap[note.id!]) {
            newMap[note.id!] = isOverflow;
          }
        }
      });
      return newMap;
    });
  }, [notes]);

  const handleSend = () => {
    if (!input.trim() || !replyingNote?.fileId) return;
    onSendMessage(input, replyingNote?.fileId);
    setInput('');
    setReplyingNote(null);
  };

  return (
    <Stack sx={{ height: { md: height - 320, xs: height - 180 } }} p={isMobile ? 0 : 2}>
      <Stack
        sx={{
          height: {
            md: !replyingNote ? height - 300 : height - 400,
            xs: height - 80
          }
        }}
        pr={1}
        overflow="auto"
        gap={2}>
        {notes?.map((note) => {
          const isExpanded = expandedMap[note.id!] || false;
          const showToggle = showToggleMap[note.id!] || false;

          return (
            <Stack
              flexDirection={'row'}
              alignItems={'start'}
              justifyContent={'space-between'}
              key={note.id}
              border={2}
              borderColor={replyingNote?.id === note.id ? '#1976d2' : '#686E7D'}
              borderRadius={2}
              p={1.5}
              maxWidth="100%"
              bgcolor={replyingNote?.id === note.id ? '#E3F2FD' : '#fff'}>
              <Stack width={'100%'}>
                <Typography fontWeight="bold" variant="body2" color="text.secondary" mb={0.5}>
                  {note.file?.clientOption?.value} - {note.file.fileNumber} (
                  {['ADMIN', 'ROOT_ADMIN'].includes(note?.user?.role)
                    ? 'Admin'
                    : note.user.firstName + ' ' + note.user.lastName}
                  )
                </Typography>

                <Typography
                  variant="body1"
                  color="text.primary"
                  ref={(el) => {
                    refsMap.current[note.id!] = el;
                  }}
                  sx={{
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: isExpanded ? 'none' : 2,
                    overflow: 'hidden'
                  }}>
                  {renderWithLinks(note.content)}
                </Typography>

                {showToggle && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() =>
                      setExpandedMap((prev) => ({ ...prev, [note.id!]: !prev[note.id!] }))
                    }>
                    {isExpanded ? 'Show less ▲' : 'Read more ▼'}
                  </Typography>
                )}
              </Stack>
              <Stack width={'120px'} alignItems={'flex-end'}>
                <Typography fontWeight="500" color="text.dark1" mb={0.5}>
                  {dayjs(note.createdAt).format('DD-MM HH:mm')}
                </Typography>
                <Stack flexDirection={'row'} gap={1}>
                  <ReplyOutlinedIcon
                    onClick={() => handleReplyMessage(note)}
                    sx={{ cursor: 'pointer', color: '#1976d2' }}
                  />
                  <img
                    onClick={() => handleDeleteMessage(Number(note.id))}
                    style={{ cursor: 'pointer' }}
                    src={'/icons/icon_delete.svg'}
                    alt={'icon'}
                    width={24}
                  />
                </Stack>
              </Stack>
            </Stack>
          );
        })}
        {hasMoreMessage && (
          <Stack display="flex" alignItems={'center'} mt={2} justifyContent="center">
            <Button
              size="small"
              style={{ width: '100px', display: 'flex', justifyContent: 'center' }}
              variant="outlined"
              onClick={fetchMoreChatDetail}
              disabled={isMoreChat}>
              {isMoreChat ? <span className="loader" /> : 'Load more...'}
            </Button>
          </Stack>
        )}
      </Stack>
      {/* Replying section */}
      {replyingNote && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              backgroundColor: '#E3F2FD',
              border: '1px solid #90CAF9',
              borderRadius: 1,
              p: 1,
              mt: 1
            }}>
            <Typography variant="body2" color="text.primary">
              Replying to {replyingNote.file?.clientOption?.value} - {replyingNote.file.fileNumber}
            </Typography>
            <Button size="small" onClick={handleCancelReply}>
              Cancel
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} pt={1}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter note..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
            />
            <Button variant="contained" onClick={handleSend}>
              Send
            </Button>
          </Stack>
        </>
      )}
      {/* Input chat */}

      {/*<Stack sx={{ width: '100%', marginTop: '10px' }} alignItems="center">*/}
      {/*  <Link*/}
      {/*    style={{*/}
      {/*      textDecoration: 'underline',*/}
      {/*      textUnderlineOffset: '4px'*/}
      {/*    }}*/}
      {/*    to={isMobile ? `/file/${fileId}` : `/admin/files/${fileId}`}>*/}
      {/*    Go to detail*/}
      {/*  </Link>*/}
      {/*</Stack>*/}
    </Stack>
  );
};
